import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { createRosterGuard, getGates } from '@/services/api.client';

const schema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  badgeNumber: z.string().optional(),
  gateId: z.string().min(1, 'Please select a gate'),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
}

/**
 * AddRosterGuardDialog — simple form to add a guard to the roster.
 * Only requires name and gate assignment. No email, no password.
 *
 * Requirements: 1.1, 1.2
 */
export function AddRosterGuardDialog({ open, onOpenChange, propertyId }: Props) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const gatesQuery = useQuery({
    queryKey: ['gates', propertyId],
    queryFn: () => getGates(propertyId),
    enabled: open,
  });

  // Filter to only shared-mode gates (roster guards use shared tablets)
  const sharedGates = (gatesQuery.data ?? []).filter(
    (g) => (g as { deviceMode?: string }).deviceMode === 'shared',
  );

  const gateId = watch('gateId');

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      createRosterGuard(propertyId, {
        fullName: values.fullName,
        ...(values.badgeNumber ? { badgeNumber: values.badgeNumber } : {}),
        gateId: values.gateId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'users', propertyId] });
      toast.success('Guard added to roster');
      onOpenChange(false);
      reset();
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Failed to add guard');
    },
  });

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Guard to Roster</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            void handleSubmit((v) => mutation.mutate(v))(e);
          }}
        >
          <FieldGroup className="py-2">
            <Field>
              <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
              <Input id="fullName" placeholder="James Mwangi" {...register('fullName')} />
              <FieldError errors={[errors.fullName]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="badgeNumber">
                Badge / Employee Number{' '}
                <span className="text-[var(--text-tertiary)]">(optional)</span>
              </FieldLabel>
              <Input id="badgeNumber" placeholder="G-001" {...register('badgeNumber')} />
            </Field>
            <Field>
              <FieldLabel htmlFor="gateId">Assigned Gate</FieldLabel>
              {gatesQuery.isLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : sharedGates.length === 0 ? (
                <>
                  <select
                    id="gateId"
                    aria-label="Assigned Gate"
                    {...register('gateId')}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    disabled
                  >
                    <option value="">No shared gates available</option>
                  </select>
                  <p className="text-xs text-[var(--status-amber-text)]">
                    Roster guards require a shared-mode gate. Change a gate&apos;s device mode to
                    &quot;shared&quot; in Property Setup first.
                  </p>
                </>
              ) : (
                <select
                  id="gateId"
                  aria-label="Assigned Gate"
                  {...register('gateId')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a gate…</option>
                  {sharedGates.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              )}
              <FieldError errors={[errors.gateId]} />
            </Field>
          </FieldGroup>

          <p className="text-xs text-[var(--text-tertiary)] mt-2 mb-4">
            Roster guards don&apos;t need an email or password. They select their name on the gate
            tablet to start their shift.
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !gateId}>
              {mutation.isPending ? 'Adding…' : 'Add to Roster'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
